'use client';

import {
  createContext,
  useCallback,
  useEffect,
  useRef,
  type ReactNode,
} from 'react';
import { useQuery, useQueryClient } from '@tanstack/react-query';
import { useRouter, usePathname } from 'next/navigation';

import {
  SESSION_QUERY_KEY,
  sessionQueryOptions,
  useLoginMutation,
  useLogoutMutation,
  useRefreshMutation,
  useSwitchRoleMutation,
} from './auth-queries';
import {
  bindUnauthorizedSessionSync,
  unbindUnauthorizedSessionSync,
} from './unauthorized-session-bridge';
import type { AuthContextValue, LoginCredentials, LoginResponse, SwitchRoleResponse } from './types';
import { resetThemeToSystem } from '@/infrastructure/hooks/useTheme';
import { matchesAnyRoute } from '@/shared/utils/match-route';

/**
 * Time before token expiration to trigger proactive refresh.
 * Default 5 minutes; override with NEXT_PUBLIC_AUTH_REFRESH_THRESHOLD_MS for
 * short-TTL testing. At runtime the value is clamped against the actual access
 * token lifetime reported by the BFF so it can never equal or exceed it — a
 * threshold ≥ lifetime would cause a perpetual refresh loop.
 */
const CONFIGURED_REFRESH_THRESHOLD_MS = (() => {
  const raw = process.env.NEXT_PUBLIC_AUTH_REFRESH_THRESHOLD_MS;
  const parsed = raw ? parseInt(raw, 10) : NaN;
  return Number.isFinite(parsed) && parsed > 0 ? parsed : 5 * 60 * 1000;
})();

/** Minimum gap (ms) between the refresh threshold and the access token expiry. */
const REFRESH_SAFETY_MARGIN_MS = 1000;

/**
 * Web Lock name for the cross-tab proactive-refresh single-flight: only the
 * tab that wins the lock issues the POST; the rest skip and learn the new
 * expiresAt via the broadcast below (or the periodic session poll).
 */
const REFRESH_WEB_LOCK = 'pa-session-refresh';

/** BroadcastChannel used to sync the session across tabs after a refresh. */
const AUTH_SYNC_CHANNEL = 'pa-auth-sync';
const SESSION_REFRESHED_MESSAGE = 'session-refreshed';

/**
 * Clamp the configured threshold against the real access token lifetime so the
 * refresh condition can never be satisfied by a freshly minted token. When the
 * BFF does not report `accessTokenLifetimeMs` (older sessions), the configured
 * value is used as-is.
 */
function resolveRefreshThreshold(accessTokenLifetimeMs: number | undefined): number {
  if (!accessTokenLifetimeMs || accessTokenLifetimeMs <= 0) {
    return CONFIGURED_REFRESH_THRESHOLD_MS;
  }
  const safeCap = Math.max(0, accessTokenLifetimeMs - REFRESH_SAFETY_MARGIN_MS);
  return Math.min(CONFIGURED_REFRESH_THRESHOLD_MS, safeCap);
}

export const AuthContext = createContext<AuthContextValue | null>(null);

interface AuthProviderProps {
  children: ReactNode;
}

/** Routes that don't require an active session */
const UNAUTHENTICATED_ROUTES = [
  '/login',
  '/forgot-password',
  '/access-denied',
  '/onboard-invitation',
  '/reset-password',
];

export function AuthProvider({ children }: AuthProviderProps) {
  const router = useRouter();
  const pathname = usePathname();
  const queryClient = useQueryClient();
  const { data: session, isLoading } = useQuery(sessionQueryOptions);

  useEffect(() => {
    bindUnauthorizedSessionSync(queryClient);
    return () => unbindUnauthorizedSessionSync();
  }, [queryClient]);
  const loginMutation = useLoginMutation();
  const logoutMutation = useLogoutMutation();
  const refreshMutation = useRefreshMutation();
  const switchRoleMutation = useSwitchRoleMutation();
  const refreshingRef = useRef(false);
  // mutateAsync is referentially stable across renders (unlike the mutation
  // result object), so callbacks/effects depending on it don't churn.
  const { mutateAsync: refreshSessionAsync } = refreshMutation;

  // Cross-tab sync: when another tab refreshes the session, pick up the new
  // expiresAt immediately instead of waiting for the periodic poll.
  useEffect(() => {
    if (typeof BroadcastChannel === 'undefined') return;
    const channel = new BroadcastChannel(AUTH_SYNC_CHANNEL);
    channel.onmessage = (event) => {
      if (event.data === SESSION_REFRESHED_MESSAGE) {
        queryClient.invalidateQueries({ queryKey: SESSION_QUERY_KEY });
      }
    };
    return () => channel.close();
  }, [queryClient]);

  const runProactiveRefresh = useCallback(async () => {
    try {
      await refreshSessionAsync();
      if (typeof BroadcastChannel !== 'undefined') {
        const channel = new BroadcastChannel(AUTH_SYNC_CHANNEL);
        channel.postMessage(SESSION_REFRESHED_MESSAGE);
        channel.close();
      }
    } catch {
      // Transient failures were already retried by the mutation and the BFF
      // reactive refresh recovers on the next request; a definitive 401 has
      // already cleared the local session state via the mutation's onError.
    }
  }, [refreshSessionAsync]);

  const triggerProactiveRefresh = useCallback(() => {
    if (refreshingRef.current) return;
    refreshingRef.current = true;
    const release = () => {
      refreshingRef.current = false;
    };

    if (typeof navigator !== 'undefined' && 'locks' in navigator) {
      // Cross-tab single-flight: only the tab that wins the lock refreshes.
      // The lock is held while the request runs, so losing tabs skip instead
      // of queueing duplicate refreshes.
      void navigator.locks
        .request(REFRESH_WEB_LOCK, { ifAvailable: true }, async (lock) => {
          if (!lock) return;
          await runProactiveRefresh();
        })
        .finally(release);
    } else {
      void runProactiveRefresh().finally(release);
    }
  }, [runProactiveRefresh]);

  // Proactive token refresh when nearing expiry
  useEffect(() => {
    if (!session?.authenticated || !session.expiresAt) return;

    const timeUntilExpiry = session.expiresAt - Date.now();
    const refreshThreshold = resolveRefreshThreshold(session.accessTokenLifetimeMs);

    // Refresh immediately if token is expired or about to expire
    if (timeUntilExpiry <= refreshThreshold) {
      triggerProactiveRefresh();
      return;
    }

    // Schedule refresh for when we hit the threshold
    const delay = timeUntilExpiry - refreshThreshold;
    const timer = setTimeout(triggerProactiveRefresh, delay);

    return () => clearTimeout(timer);
  }, [
    session?.authenticated,
    session?.expiresAt,
    session?.accessTokenLifetimeMs,
    triggerProactiveRefresh,
  ]);

  // Redirect to login when session is no longer valid
  useEffect(() => {
    if (isLoading) return;
    if (session?.authenticated) return;

    const isProtectedRoute = !matchesAnyRoute(pathname, UNAUTHENTICATED_ROUTES);

    if (isProtectedRoute) {
      router.push(`/login?callbackUrl=${encodeURIComponent(pathname)}`);
    }
  }, [isLoading, session?.authenticated, pathname, router]);

  const login = useCallback(
    async (credentials: LoginCredentials): Promise<LoginResponse> => {
      return loginMutation.mutateAsync(credentials);
    },
    [loginMutation],
  );

  const handleLogout = useCallback(async () => {
    await logoutMutation.mutateAsync();
    resetThemeToSystem();
    router.push('/login');
  }, [logoutMutation, router]);

  const handleRefreshSession = useCallback(async () => {
    await refreshMutation.mutateAsync();
  }, [refreshMutation]);

  const handleSwitchRole = useCallback(
    async (tenantUuid: string, roleUuid: string): Promise<SwitchRoleResponse> => {
      return switchRoleMutation.mutateAsync({ tenantUuid, roleUuid });
    },
    [switchRoleMutation],
  );

  const value: AuthContextValue = {
    user: session?.user ?? null,
    requirePasswordReset: session?.prr === true,
    isAuthenticated: session?.authenticated ?? false,
    isLoading,
    tenantUuid: session?.tenantUuid ?? null,
    tenantName: session?.tenantName ?? null,
    tenantType: session?.tenantType ?? null,
    preferences: session?.preferences ?? null,
    roleCode: session?.roleCode ?? null,
    roleName: session?.roleName ?? null,
    availablePlatformRoles: session?.availablePlatformRoles ?? [],
    login,
    logout: handleLogout,
    refreshSession: handleRefreshSession,
    switchRole: handleSwitchRole,
  };

  return <AuthContext.Provider value={value}>{children}</AuthContext.Provider>;
}
